import { useState, useRef, useCallback, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import { Box } from "@mui/material";

import GinoHeader from "../../components/GinoHeader";
import GinoChatInput from "../../components/GinoChatInput";
import MessageBubble from "../../components/MessageBubble";
import TypingIndicator from "../../components/TypingIndicator";
import ProgressBar from "../../components/ProgressBar";
import ClarificationChips from "../../components/ClarificationChips";
import {
  EventCard, ProgramCard, PlayerCard, CoachCard, ScoutCard, AgentCard, ChallengeCard, CourseCard, ComparisonCard, CampaignCard,
  TrainingPlanCard, TrainingSketchCard, NutritionPlanCard, TeamPlanCard, SummaryCard,
  ErrorCard, PaymentRequiredCard, JobCard, JobCreatedCard, EventCreatedCard, ServiceCreatedCard, TalentSessionCard, TalentReportCard,
} from "../../components/ResultCards";
import FollowUpActions from "../../components/FollowUpActions";
import { ginoQuery, ginoClarify, ginoGetJob, replyTalentReport, downloadTalentReportPdf } from "../../api/services/ginoQuery";
import { useGetGinoCredits } from "../../api/queries/ginoCredits";
import { GINO_QUERY_KEYS } from "../../api/queryKeys";
import { useGinoChatRoom } from "../../hooks/useGinoChatRoom";
import { reconstructCard } from "../../utils/reconstructCard";
import type { ChatMessage, ClarificationQuestion, TalentSession, JobOutput, Cta } from "../../types/ginoQuery";
import type { GinoMessageMeta } from "../../types/chatPersistence";

// ========== TYPES ==========
type FeedItem =
  | { type: "message"; id: string; data: ChatMessage }
  | { type: "card"; id: string; data: React.ReactNode }
  | { type: "clarification"; id: string; data: ClarificationQuestion };

const POLL_INTERVAL = 1500;
// Client-side polling budget. The backend reaper flips genuinely-stuck jobs to
// failed ~10min out; this stops the UI from spinning forever well before that.
const POLL_TIMEOUT_MS = 180_000;
const INTENT_LABEL_KEYS: Record<string, string> = { find_training_event: "intentTrainingEvents", training_plan_me: "intentTrainingPlan", summarizer: "intentSummarizer", generate_talent_report: "intentTalentReport", find_hire_job: "intentJobSearch", create_hire_job: "intentCreateJob", create_event: "intentCreateEvent", unknown: "intentGeneral" };
const nowTime = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
let feedIdCounter = 0;
const nextFeedId = () => `feed-${++feedIdCounter}-${Date.now()}`;

// Monotonic client sequence stamped into every persisted message's meta. Gives a
// deterministic tiebreak when a narration + its cards persist within the same
// created_at millisecond, so the transcript order is stable on resume.
let seqCounter = 0;
const nextSeq = () => ++seqCounter;

// CTAs shown under a talent report (live + restored from history). The reportId
// rides along as prefillSlots so "Build a plan" works for ghost players and on
// resume of an old chat (where the reportId is persisted in the card data).
const buildTalentReportCtas = (reportId?: string): Cta[] => [
  ...(reportId ? [{ label: "Download PDF", action: "download" as const, url: `report:${reportId}` }] : []),
  {
    label: "Build a plan for this player",
    action: "intent" as const,
    intent: "training_plan_player",
    ...(reportId ? { prefillSlots: { reportId } } : {}),
  },
  { label: "New Evaluation", action: "intent" as const, intent: "generate_talent_report" },
];

const GinoChatPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation("gino", { keyPrefix: "chatPage" });
  // Present when resuming a past conversation from history (/gino/chat-history/:chatId).
  const { chatId: chatIdParam } = useParams();
  const initialMessage = (location.state as any)?.message;

  // ========== UNIFIED FEED STATE ==========
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  // True while a clarification/ask_user question is awaiting the user's answer —
  // drives the input placeholder so open-ended agent questions have an affordance.
  const [awaitingAnswer, setAwaitingAnswer] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const [intentBadge, setIntentBadge] = useState<string | undefined>();
  const [progress, setProgress] = useState<{ pct: number; step?: string } | null>(null);
  const [talentSession, setTalentSession] = useState<TalentSession | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pendingClarificationRef = useRef<{ conversationId: string; questions: ClarificationQuestion[] } | null>(null);
  const lastMessageRef = useRef<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartRef = useRef<number>(0);
  // Mirrors isProcessing for synchronous re-entrancy checks in handlers that fire
  // from CTA buttons / chips (which aren't disabled during processing).
  const isProcessingRef = useRef(false);
  const hasSentInitial = useRef(false);
  const shouldScrollRef = useRef(false);
  const workingMsgIdRef = useRef<string | null>(null);
  const handleSendRef = useRef<(text: string, forcedIntent?: string) => void>(() => {});
  const dispatchCtaRef = useRef<(cta: Cta) => void>(() => {});
  // The most recent conversation whose turn actually executed (not left
  // mid-clarification), recovered from history meta. On a resumed chat it's sent
  // alongside chatId so the backend can locate the prior turn for context.
  const lastExecutedConversationIdRef = useRef<string | undefined>(undefined);

  const { data: ginoCredits, isLoading: isLoadingGinoCredits, error: errorGinoCredits } = useGetGinoCredits();
  const [openCreditLeftModal, setOpenCreditLeftModal] = useState(false);

  const { chatId, isRoomReady, persistMessage, loadHistory } = useGinoChatRoom(chatIdParam);

  // Keep the ref in sync so re-entrancy guards see the latest value synchronously,
  // regardless of which code path last flipped isProcessing.
  useEffect(() => { isProcessingRef.current = isProcessing; }, [isProcessing]);

  const isNearBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  }, []);

  const scrollToBottom = useCallback((instant = false) => {
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: instant ? "auto" : "smooth",
      });
    });
  }, []);

  // Smart scroll: only when user is near bottom or explicitly requested
  useEffect(() => {
    if (shouldScrollRef.current) {
      shouldScrollRef.current = false;
      scrollToBottom();
    } else if (isNearBottom()) {
      scrollToBottom();
    }
  }, [feed, showTyping, scrollToBottom, isNearBottom]);

  // ========== HISTORY RESTORE ==========
  useEffect(() => {
    if (!isRoomReady || historyLoaded) return;
    setHistoryLoaded(true);

    const restore = async () => {
      const history = await loadHistory();
      if (!history.length) {
        // First-time user — show intro message in chat
        const introId = nextFeedId();
        const introMsg: ChatMessage = {
          id: introId, role: "gino",
          text: t("intro"),
          time: nowTime(), isMarkdown: true,
        };
        setFeed([{ type: "message", id: introId, data: introMsg }]);
        return;
      }

      const restored: FeedItem[] = [];
      // Track the latest in-progress (collecting) clarification so we can resume it.
      // A user message after the prompt means it was already answered → clear it.
      let resume: { conversationId: string; questions: ClarificationQuestion[]; question: ClarificationQuestion } | null = null;

      for (const msg of history) {
        let meta: GinoMessageMeta | null = null;
        try { if (msg.meta) meta = JSON.parse(msg.meta); } catch {}

        const role: "user" | "gino" = meta?.role || "user";
        const isMarkdown = meta?.isMarkdown || false;

        if (role === "user") resume = null;
        else if (meta?.conversationId && meta?.clarificationQuestion) {
          resume = { conversationId: meta.conversationId, questions: [meta.clarificationQuestion], question: meta.clarificationQuestion };
        }
        // Remember the most recent conversation id seen in history (sent on resume
        // as a secondary linkage; the chat room id is the primary one).
        if (meta?.conversationId) lastExecutedConversationIdRef.current = meta.conversationId;

        // Add text message (skip card placeholders like "[player result]")
        const isCardPlaceholder = meta?.cardType && /^\[.+ result\]$/.test(msg.text);
        if (!isCardPlaceholder) {
          restored.push({
            type: "message",
            id: `hist-${msg.id}`,
            data: {
              id: `hist-${msg.id}`,
              role,
              text: msg.text,
              time: new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              isMarkdown,
            },
          });
        }

        // Add card right after its associated message
        if (meta?.cardType && meta?.cardData) {
          const card = reconstructCard(
            meta.cardType,
            meta.cardData,
            `hist-card-${msg.id}`,
            cta => dispatchCtaRef.current(cta),
            () => setOpenCreditLeftModal(true),
          );
          if (card) {
            restored.push({ type: "card", id: `hist-card-${msg.id}`, data: card });
          }
          // Re-attach the talent-report CTAs so "Build a plan for this player"
          // works on resume — the reportId is persisted in the card data.
          if (meta.cardType === "talent_report") {
            const reportId = meta.cardData?.report_id;
            restored.push({
              type: "card",
              id: `hist-cta-${msg.id}`,
              data: (
                <FollowUpActions
                  key={`hist-cta-${msg.id}`}
                  ctas={buildTalentReportCtas(reportId)}
                  onCta={cta => dispatchCtaRef.current(cta)}
                />
              ),
            });
          }
        }
      }

      // Re-attach to the in-progress conversation: next answer routes through
      // ginoClarify, and re-render the option chips if the question had any.
      if (resume) {
        pendingClarificationRef.current = { conversationId: resume.conversationId, questions: resume.questions };
        setAwaitingAnswer(true);
        if (resume.question.options?.length) {
          restored.push({ type: "clarification", id: nextFeedId(), data: resume.question });
        }
      }

      setFeed(restored);
      // Scroll to bottom instantly (no animation) after history restore
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    };
    restore();
  }, [isRoomReady, historyLoaded, loadHistory]);

  // Send initial message from home page (fresh chats only — never replay a stale
  // location.state message when resuming a specific room from history).
  useEffect(() => {
    if (initialMessage && !chatIdParam && !hasSentInitial.current && isRoomReady && historyLoaded) {
      hasSentInitial.current = true;
      handleSend(initialMessage);
    }
  }, [initialMessage, chatIdParam, isRoomReady, historyLoaded]);

  // Invalidate history cache on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      queryClient.invalidateQueries({ queryKey: GINO_QUERY_KEYS.CHAT_LIST });
      if (chatId) queryClient.invalidateQueries({ queryKey: GINO_QUERY_KEYS.CHAT_MESSAGES(String(chatId)) });
    };
  }, [chatId, queryClient]);

  // ========== FEED HELPERS ==========
  const addMessage = useCallback((role: "user" | "gino", text: string, isMarkdown = false, extraMeta?: Partial<GinoMessageMeta>, step?: { current: number; total: number }) => {
    const id = nextFeedId();
    const msg: ChatMessage = { id, role, text, time: nowTime(), isMarkdown, ...(step ? { step } : {}) };
    setFeed(prev => [...prev, { type: "message", id, data: msg }]);
    // Always scroll for user messages; smart scroll for GINO messages
    if (role === "user") shouldScrollRef.current = true;
    const meta: Partial<GinoMessageMeta> = { messageType: isMarkdown ? "markdown" : "text", isMarkdown, seq: nextSeq(), ...extraMeta };
    persistMessage(text, role, meta).catch(() => {});
  }, [persistMessage]);

  const addCard = useCallback((card: React.ReactNode, cardType?: string, cardData?: any) => {
    const id = nextFeedId();
    setFeed(prev => [...prev, { type: "card", id, data: card }]);
    // Persist card to chat-ws
    if (cardType && cardData) {
      const meta: Partial<GinoMessageMeta> = { messageType: "result_card", cardType, cardData, seq: nextSeq() };
      persistMessage(`[${cardType} result]`, "gino", meta).catch(() => {});
    }
  }, [persistMessage]);

  const addClarification = useCallback((question: ClarificationQuestion) => {
    const id = nextFeedId();
    setFeed(prev => [...prev, { type: "clarification", id, data: question }]);
  }, []);

  const removeClarifications = useCallback(() => {
    setFeed(prev => prev.filter(item => item.type !== "clarification"));
  }, []);

  const handleError = useCallback((error: any) => {
    if (error?.response?.status === 402) {
      const payData = error?.response?.data?.data;
      addCard(<PaymentRequiredCard key={`pay-${Date.now()}`} data={payData} onBuyCredits={() => setOpenCreditLeftModal(true)} />, "payment_required", payData || {});
    } else {
      const msg = error?.response?.data?.error || error?.response?.data?.message || error?.message || t("unknownError");
      addCard(<ErrorCard key={`err-${Date.now()}`} message={msg} retryable onRetry={() => { if (lastMessageRef.current) handleSend(lastMessageRef.current); }} />, "error", { message: msg });
    }
  }, [addCard]);

  // Billing happens backend-side after a job settles, so refresh the credit
  // balance once a job reaches a terminal state (completed OR failed — a failed
  // job may still have incurred classification billing).
  const refreshCredits = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: GINO_QUERY_KEYS.GINO_CREDITS });
  }, [queryClient]);

  // Drop the transient "Working on it..." message and stop polling (shared by
  // the failed and timed-out paths).
  const clearWorkingState = useCallback(() => {
    if (workingMsgIdRef.current) {
      setFeed(prev => prev.filter(item => item.id !== workingMsgIdRef.current));
      workingMsgIdRef.current = null;
    }
    setProgress(null);
    setIsProcessing(false);
  }, []);

  // ========== JOB POLLING ==========
  const pollJob = useCallback(async (jobId: string) => {
    try {
      const res = await ginoGetJob(jobId);
      const job = res.data || res;
      setProgress({ pct: job.progress || 0, step: job.currentStep });

      if (job.status === "completed") {
        setProgress({ pct: 100, step: job.currentStep });
        setTimeout(() => { setProgress(null); renderJobResult(job); setIsProcessing(false); }, 400);
        refreshCredits();
      } else if (job.status === "failed") {
        clearWorkingState();
        addCard(<ErrorCard key={`err-${Date.now()}`} message={job.error || t("jobFailed")} retryable onRetry={() => { if (lastMessageRef.current) handleSend(lastMessageRef.current); }} />);
        refreshCredits();
      } else if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        // The job is still running after the client budget. Stop polling and give the
        // user a retry; the backend reaper flips the row to failed shortly after.
        clearWorkingState();
        addCard(<ErrorCard key={`timeout-${Date.now()}`} message={t("jobTimeout")} retryable onRetry={() => { if (lastMessageRef.current) handleSend(lastMessageRef.current); }} />);
        refreshCredits();
      } else {
        pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_INTERVAL);
      }
    } catch (err) { setProgress(null); handleError(err); setIsProcessing(false); }
  }, [addCard, clearWorkingState, handleError, refreshCredits, t]);

  // ========== CTA DISPATCH (backend-driven) ==========
  const dispatchCta = useCallback((cta: Cta) => {
    // Intent CTAs start a new turn — block while one is in flight (link/download are safe).
    if (cta.action === "intent" && isProcessingRef.current) return;
    if (cta.action === "download" && cta.url?.startsWith("report:")) {
      // Talent-report PDF download (shared by live + restored CTAs).
      downloadTalentReportPdf(cta.url.slice("report:".length)).catch(() => {});
    } else if (cta.action === "intent") {
      // Re-submit as a new turn; prefilled slots ride along as a natural-language hint.
      const hint = cta.prefillSlots
        ? ` (${Object.entries(cta.prefillSlots)
            .map(([k, v]) => `${k}: ${v}`)
            .join(", ")})`
        : "";
      // Pass the CTA's target intent so the backend skips re-classification of the
      // chip label (e.g. "Report: …" must not be mis-routed to a training plan).
      handleSendRef.current(`${cta.label}${hint}`, cta.intent);
    } else if (cta.url) {
      // Internal app routes navigate in-app; absolute URLs open in a new tab.
      if (/^https?:\/\//.test(cta.url)) window.open(cta.url, "_blank", "noopener");
      else navigate(cta.url);
    }
  }, [navigate]);
  dispatchCtaRef.current = dispatchCta;

  // ========== RENDER JOB RESULTS ==========
  const renderJobResult = useCallback((job: any) => {
    // Remove the transient "Working on it..." message
    if (workingMsgIdRef.current) {
      setFeed(prev => prev.filter(item => item.id !== workingMsgIdRef.current));
      workingMsgIdRef.current = null;
    }
    const outputs: JobOutput[] = job.result?.outputs || [];
    const narrateOutput = outputs.find(o => o.step === "narrate_results");
    // The agent's ask_user question, if any — persist it onto the narration message
    // so reopening the chat re-attaches to the collecting conversation (mirrors the
    // needs_clarification path). Hoisted above the narration render for that meta.
    const agentQuestion: ClarificationQuestion | undefined = job.result?.question;
    const questionMeta: Partial<GinoMessageMeta> | undefined =
      agentQuestion && job.conversationId ? { conversationId: job.conversationId, clarificationQuestion: agentQuestion } : undefined;

    // Tracks whether the turn produced any visible output; drives the empty-result
    // fallback so a completed job that rendered nothing isn't a silent blank turn.
    let renderedSomething = false;
    if (narrateOutput?.output?.narration) {
      // Defensive: strip any markdown image the model embedded — result media are
      // bare filenames that render as broken <img>. (Backend also strips these.)
      const narration = (narrateOutput.output.narration as string).replace(/!\[[^\]]*\]\([^)]*\)/g, "").trim();
      addMessage("gino", marked.parse(narration) as string, true, questionMeta);
      renderedSomething = true;
    } else if (questionMeta) {
      // No narration but the agent asked a question — persist the question text with meta.
      addMessage("gino", agentQuestion!.question, false, questionMeta);
      renderedSomething = true;
    }

    const send = (msg: string) => handleSendRef.current(msg);

    // Per-row CTAs arrive attached to each item (item.ctas) from the backend and
    // render inline inside the PlayerCard / EventCard rows. result.ctas holds only
    // the generic/trailing CTAs.
    const allCtas: Cta[] = job.result?.ctas || [];

    // Courses reuse the search_services step; distinguish by the job's intent so
    // the same output renders as a CourseCard for find_course.
    const isCourseIntent = job.intent === "find_course";
    // Steps that always render a card when reached (used to mark the turn non-empty).
    const ALWAYS_RENDERS = new Set([
      "search_events", "search_programs", "search_services", "search_talent", "search_coaches",
      "search_scouts", "search_agents", "search_challenges",
      "compare_profiles", "build_campaign_audience", "generate_training_plan", "generate_nutrition_plan",
      "generate_team_plan", "summarize_notes", "search_jobs", "create_job_draft", "create_event_draft",
      "create_service_draft", "start_talent_report_session",
    ]);
    // The agent sometimes runs the same search twice (e.g. one country per nationality),
    // producing an empty "No X found" card alongside a populated one. Suppress the empty
    // duplicates: for each step, if any output has results, drop that step's empty outputs.
    const stepHasResults = new Set(
      outputs.filter(o => ((o.output as { count?: number })?.count ?? 0) > 0).map(o => o.step),
    );
    const visibleOutputs = outputs.filter(o => {
      const count = (o.output as { count?: number })?.count;
      return !(count === 0 && stepHasResults.has(o.step));
    });

    for (const o of visibleOutputs) {
      if (o.step === "narrate_results" || o.step === "get_user_profile" || o.step === "get_talent_insights") continue;
      if (ALWAYS_RENDERS.has(o.step)) renderedSomething = true;
      switch (o.step) {
        case "search_events":
          addCard(<EventCard key={`evt-${Date.now()}`} output={o.output} onCta={dispatchCta} />, "event", o.output);
          break;
        case "search_programs": case "search_services":
          if (isCourseIntent) addCard(<CourseCard key={`course-${Date.now()}`} output={o.output} onCta={dispatchCta} />, "course", o.output);
          else addCard(<ProgramCard key={`prog-${Date.now()}-${o.step}`} output={o.output} />, "program", o.output);
          break;
        case "search_talent": {
          addCard(<PlayerCard key={`plyr-${Date.now()}`} output={o.output} onCta={dispatchCta} />, "player", o.output);
          break;
        }
        case "search_coaches":
          addCard(<CoachCard key={`coach-${Date.now()}`} output={o.output} onCta={dispatchCta} />, "coach", o.output);
          break;
        case "search_scouts":
          addCard(<ScoutCard key={`scout-${Date.now()}`} output={o.output} onCta={dispatchCta} />, "scout", o.output);
          break;
        case "search_agents":
          addCard(<AgentCard key={`agent-${Date.now()}`} output={o.output} onCta={dispatchCta} />, "agent", o.output);
          break;
        case "search_challenges":
          addCard(<ChallengeCard key={`chal-${Date.now()}`} output={o.output} onCta={dispatchCta} />, "challenge", o.output);
          break;
        case "compare_profiles":
          addCard(<ComparisonCard key={`cmp-${Date.now()}`} output={o.output} />, "comparison", o.output);
          break;
        case "build_campaign_audience":
          addCard(<CampaignCard key={`camp-${Date.now()}`} output={o.output} onAction={(msg: string) => send(msg)} />, "campaign", o.output);
          break;
        case "generate_training_plan": {
          addCard(<TrainingPlanCard key={`tplan-${Date.now()}`} output={o.output} />, "training_plan", o.output);
          break;
        }
        case "generate_training_sketch":
          if (o.output?.images?.length) {
            // Don't persist sketch card data to chat-ws — image data is too large (base64)
            addCard(<TrainingSketchCard key={`tsketch-${Date.now()}`} output={o.output} />);
          }
          break;
        case "generate_nutrition_plan":
          addCard(<NutritionPlanCard key={`nplan-${Date.now()}`} output={o.output} />, "nutrition_plan", o.output);
          break;
        case "generate_team_plan":
          addCard(<TeamPlanCard key={`team-${Date.now()}`} output={o.output} />, "team_plan", o.output);
          break;
        case "summarize_notes":
          addCard(<SummaryCard key={`sum-${Date.now()}`} output={o.output} />, "summary", o.output);
          break;
        case "search_jobs":
          addCard(<JobCard key={`job-${Date.now()}`} output={o.output} />, "hire_job", o.output);
          break;
        case "create_job_draft":
          addCard(<JobCreatedCard key={`jobcr-${Date.now()}`} output={o.output} />, "hire_job_created", o.output);
          break;
        case "create_event_draft":
          addCard(<EventCreatedCard key={`evtcr-${Date.now()}`} output={o.output} />, "event_created", o.output);
          break;
        case "create_service_draft":
          addCard(<ServiceCreatedCard key={`svccr-${Date.now()}`} output={o.output} />, "service_created", o.output);
          break;
        case "start_talent_report_session":
          if (o.output.kind === "error") addCard(<ErrorCard key={`terr-${Date.now()}`} message={o.output.message} />);
          else if (o.output.kind === "talent_report_session_started") {
            setTalentSession({ threadId: o.output.threadId, runId: o.output.runId });
            addCard(<TalentSessionCard key={`tsess-${Date.now()}`} output={o.output} onSelectPlayer={(id, name) => handleTalentReply(`I want to evaluate ${name} (Player ID: ${id})`)} onAddNewPlayer={() => handleTalentReply("I want to evaluate a player who is not in my network")} />);
          }
          break;
        case "fallback_response": if (!narrateOutput?.output?.narration) { addMessage("gino", o.output.message || t("fallbackResponse")); renderedSomething = true; } break;
        default: break;
      }
    }

    // Backend-driven generic/trailing follow-up CTAs (localized, persona-aware).
    // Per-item CTAs are already rendered inline within their card rows. Persist as
    // a `follow_up_actions` card (cardData = {ctas, rtl}) so the buttons survive
    // resume — reconstructCard rebuilds them wired to dispatchCtaRef.
    if (allCtas.length) {
      const rtl = /[\u0600-\u06FF]/.test(allCtas[0]?.label || "");
      addCard(
        <FollowUpActions key={`ctas-${Date.now()}`} ctas={allCtas} onCta={dispatchCta} rtl={rtl} />,
        "follow_up_actions",
        { ctas: allCtas, rtl },
      );
      renderedSomething = true;
    }

    // Agent ask_user: the loop asked a clarifying question and left the conversation
    // open (backend keeps it "collecting"). Arm pendingClarification with the job's
    // conversationId so the user's next message routes through ginoClarify back to THIS
    // conversation (not a fresh, context-less turn), render option chips when provided,
    // and cue the input placeholder. The question text + resume meta were already
    // persisted with the narration message above.
    if (agentQuestion && job.conversationId) {
      pendingClarificationRef.current = { conversationId: job.conversationId, questions: [agentQuestion] };
      setAwaitingAnswer(true);
      if (agentQuestion.options?.length) addClarification(agentQuestion);
    }

    // Nothing recognizable rendered and no question was asked \u2014 surface a generic
    // completion instead of a silent blank turn (e.g. an unknown step on version skew).
    if (!renderedSomething && !agentQuestion) {
      addMessage("gino", t("genericDone"));
    }
  }, [addCard, addClarification, addMessage, dispatchCta, t]);

  // ========== HANDLE QUERY RESPONSE ==========
  const handleQueryResponse = useCallback(async (data: any) => {
    if (data.intent) setIntentBadge(INTENT_LABEL_KEYS[data.intent] ? t(INTENT_LABEL_KEYS[data.intent]) : data.intent);

    // Show personalized intro from backend on first-time interaction
    if (data.firstTimeIntro) {
      addMessage("gino", marked.parse(data.firstTimeIntro) as string, true);
    }

    if (data.status === "needs_clarification") {
      const questions: ClarificationQuestion[] = data.clarificationQuestions || [];
      pendingClarificationRef.current = { conversationId: data.conversationId, questions: [...questions] };
      setAwaitingAnswer(true);
      // Wizard progress for multi-step flows — rendered as a small "Step X of N"
      // badge above the bubble (NOT baked into the message text, which previously
      // showed raw "_Step 1 of 6_" underscores). One question per turn, so the badge
      // rides on that single question.
      const step =
        typeof data.stepIndex === "number" && typeof data.totalSteps === "number" && data.totalSteps > 1
          ? { current: data.stepIndex, total: data.totalSteps }
          : undefined;
      for (const q of questions) {
        // Persist the active conversationId + the question on each clarification
        // prompt so reopening the chat re-attaches to the collecting conversation.
        addMessage("gino", q.question, false, { conversationId: data.conversationId, clarificationQuestion: q }, step);
        if (q.options?.length) addClarification(q);
      }
      setIsProcessing(false);
    } else if (data.status === "queued") {
      pendingClarificationRef.current = null;
      setAwaitingAnswer(false);
      // Non-persisted "Working on it..." message (removed when results arrive)
      const wId = nextFeedId();
      const wMsg: ChatMessage = { id: wId, role: "gino", text: t("workingOnIt"), time: nowTime() };
      setFeed(prev => [...prev, { type: "message", id: wId, data: wMsg }]);
      workingMsgIdRef.current = wId;
      setProgress({ pct: 0, step: undefined });
      pollStartRef.current = Date.now();
      pollJob(data.jobId);
    } else if (data.status === "payment_required") {
      addCard(<PaymentRequiredCard key={`pay-${Date.now()}`} data={data} onBuyCredits={() => setOpenCreditLeftModal(true)} />, "payment_required", data);
      setIsProcessing(false);
    }
  }, [addCard, addClarification, addMessage, pollJob]);

  // ========== CLARIFICATION ==========
  const handleClarificationAnswer = useCallback(async (key: string, value: string) => {
    const pending = pendingClarificationRef.current;
    if (!pending) return;
    if (isProcessingRef.current) return; // guard rapid chip taps
    removeClarifications();
    setAwaitingAnswer(false);
    addMessage("user", value);
    setIsProcessing(true);
    setShowTyping(true);
    try {
      const res = await ginoClarify(pending.conversationId, { [key]: value });
      setShowTyping(false);
      pendingClarificationRef.current = null;
      await handleQueryResponse(res.data || res);
    } catch (err: any) {
      setShowTyping(false);
      // A dead conversation (deleted / already finished) must not stay armed, or the
      // next free-text message re-hits it forever. Clear so it starts a fresh turn.
      const status = err?.response?.status;
      if (status === 404 || status === 400) pendingClarificationRef.current = null;
      handleError(err);
      setIsProcessing(false);
    }
  }, [addMessage, handleError, handleQueryResponse, removeClarifications]);

  // ========== SEND MESSAGE ==========
  const handleSend = useCallback(async (text: string, forcedIntent?: string) => {
    lastMessageRef.current = text;
    if (talentSession) { handleTalentReply(text); return; }
    if (pendingClarificationRef.current) {
      const freeQ = pendingClarificationRef.current.questions.find(q => !q.options?.length);
      const q = freeQ || pendingClarificationRef.current.questions[0];
      if (q) { handleClarificationAnswer(q.key, text); return; }
    }
    // Guard against overlapping turns from rapid CTA taps (CTA buttons aren't
    // disabled during processing, unlike the text input).
    if (isProcessingRef.current) return;
    setIsProcessing(true);
    addMessage("user", text);
    setShowTyping(true);
    try {
      // On a resumed chat, send the recovered conversation id so the backend can
      // locate the prior turn; the chat room id is the primary continuity linkage.
      // We're past the pending-clarification guard above, so this is a fresh turn.
      const priorConversationId = lastExecutedConversationIdRef.current;
      const res = await ginoQuery(text, priorConversationId, forcedIntent, chatId ?? undefined);
      setShowTyping(false);
      await handleQueryResponse(res.data || res);
    } catch (err: any) {
      setShowTyping(false);
      if (err?.response?.status === 403) addCard(<ErrorCard key={`err-${Date.now()}`} message={t("authenticationFailed")} />);
      else handleError(err);
      setIsProcessing(false);
    }
  }, [addCard, addMessage, chatId, handleClarificationAnswer, handleError, handleQueryResponse, talentSession]);

  handleSendRef.current = handleSend;

  // ========== TALENT REPORT ==========
  const handleTalentReply = useCallback(async (text: string) => {
    if (!talentSession) return;
    addMessage("user", text);
    setIsProcessing(true);
    setShowTyping(true);
    try {
      const res = await replyTalentReport(talentSession.threadId, text);
      setShowTyping(false);
      const data = res.data || res;
      if (data.type === "message") addMessage("gino", marked.parse(data.message) as string, true);
      else if (data.type === "talent_report_complete") {
        addCard(<TalentReportCard key={`treport-${Date.now()}`} report={data.report} />, "talent_report", data.report);
        const reportId = data.report?.report_id;
        addCard(
          <FollowUpActions
            key={`cta-treport-${Date.now()}`}
            ctas={buildTalentReportCtas(reportId)}
            onCta={dispatchCta}
          />,
        );
        setTalentSession(null);
      }
    } catch (err) { setShowTyping(false); handleError(err); }
    setIsProcessing(false);
  }, [addCard, addMessage, handleError, talentSession, dispatchCta]);

  // ========== RENDER ==========
  return (
    <Box sx={styles.container}>
      <GinoHeader
        ginoCredits={ginoCredits}
        isLoadingGinoCredits={isLoadingGinoCredits}
        errorGinoCredits={errorGinoCredits}
        openCreditLeftModal={openCreditLeftModal}
        handleOpenCreditLeftModal={() => setOpenCreditLeftModal(true)}
        handleCloseCreditLeftModal={() => setOpenCreditLeftModal(false)}
      />
      <Box sx={styles.mainContainer}>
        <Box ref={scrollContainerRef} sx={styles.messagesContainer}>
          {feed.map((item) => {
            switch (item.type) {
              case "message":
                return <MessageBubble key={item.id} message={item.data} />;
              case "card":
                return <Box key={item.id}>{item.data}</Box>;
              case "clarification":
                return <ClarificationChips key={item.id} question={item.data} onAnswer={handleClarificationAnswer} />;
              default:
                return null;
            }
          })}
          {intentBadge && isProcessing && (
            <Box sx={{ ml: "42px", mb: "4px" }}>
              <Box component="span" sx={{ display: "inline-block", padding: "2px 10px", borderRadius: "9999px", fontSize: 11, fontWeight: 600, fontFamily: "Montserrat", background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" }}>{intentBadge}</Box>
            </Box>
          )}
          {progress && <ProgressBar progress={progress.pct} currentStep={progress.step} />}
          {showTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </Box>
        <GinoChatInput disabled={isProcessing} onSend={handleSend} placeholder={awaitingAnswer ? t("answerPlaceholder") : undefined} />
      </Box>
    </Box>
  );
};

export default GinoChatPage;

const styles = {
  container: {
    width: "100%",
    height: "90vh",
    background: "var(--background-surface-default, #FFF)",
    padding: { xs: "16px", md: "32px" },
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    overflow: "hidden",
  },
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: { xs: "0", md: "0 80px" },
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  messagesContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "auto",
    paddingRight: "12px",
    gap: "4px",
  },
};
